import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Mail, User, Phone, Sparkles, Calculator, Users, Calendar, Shield, Loader2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/components/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate simple math captcha
const generateCaptcha = () => {
  const operations = ['+', '-'];
  const operation = operations[Math.floor(Math.random() * operations.length)];
  let num1, num2;
  
  if (operation === '+') {
    num1 = Math.floor(Math.random() * 20) + 1; // 1-20
    num2 = Math.floor(Math.random() * 20) + 1; // 1-20
  } else {
    num1 = Math.floor(Math.random() * 20) + 10; // 10-29
    num2 = Math.floor(Math.random() * num1); // 0 to num1-1 (to ensure positive result)
  }
  
  const answer = operation === '+' ? num1 + num2 : num1 - num2;
  return { num1, num2, operation, answer };
};

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithGoogle, register, login, verify2FA } = useAuth();
  const [isLogin, setIsLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    phone: '',
    gender: '',
    birthDate: ''
  });

  // 2FA State
  const [show2FA, setShow2FA] = useState(false);
  const [twoFAData, setTwoFAData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);

  // Get the redirect destination (if user was redirected from a protected route)
  const from = location.state?.from?.pathname || '/';
  const pendingGuestScan = location.state?.pendingGuestScan;

  // Convert guest scan after registration
  const convertGuestScan = async () => {
    if (!pendingGuestScan) return;
    
    try {
      const scanData = JSON.parse(pendingGuestScan);
      if (scanData.readingId) {
        await axios.post(
          `${API_URL}/api/readings/convert-guest`,
          new URLSearchParams({ guest_reading_id: scanData.readingId }),
          { withCredentials: true }
        );
        // Clear the localStorage
        localStorage.removeItem('reveliae_guest_scan');
        toast.success('Votre lecture a été sauvegardée dans votre historique !');
      }
    } catch (error) {
      console.error('Error converting guest scan:', error);
    }
  };

  // Regenerate captcha when switching to register mode
  useEffect(() => {
    if (!isLogin) {
      setCaptcha(generateCaptcha());
      setCaptchaAnswer('');
    }
  }, [isLogin]);

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  const refreshCaptcha = () => {
    setCaptcha(generateCaptcha());
    setCaptchaAnswer('');
  };

  const handleClassicAuth = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email);
        
        // Check if 2FA is required
        if (result.requires_2fa) {
          setTwoFAData(result);
          setShow2FA(true);
          
          // If method is email, the backend already sent the code
          if (result.method === 'email') {
            toast.success('Code de vérification envoyé par email');
          } else {
            toast.info('Entrez le code de votre application d\'authentification');
          }
          setIsLoading(false);
          return;
        }
        
        toast.success('Connexion réussie !');
        navigate(from, { replace: true });
      } else {
        // Validate captcha for registration
        if (parseInt(captchaAnswer) !== captcha.answer) {
          toast.error('Réponse au calcul incorrecte. Veuillez réessayer.');
          refreshCaptcha();
          setIsLoading(false);
          return;
        }
        
        if (!formData.email || !formData.firstName) {
          toast.error('Email et prénom sont obligatoires');
          setIsLoading(false);
          return;
        }
        await register(formData.email, formData.firstName, formData.phone, formData.gender, formData.birthDate);
        
        // Convert guest scan if pending
        await convertGuestScan();
        
        toast.success('Inscription réussie !');
        navigate(pendingGuestScan ? '/history' : from, { replace: true });
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Une erreur est survenue';
      toast.error(message);
      if (!isLogin) {
        refreshCaptcha();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Veuillez entrer un code à 6 chiffres');
      return;
    }
    
    setIsVerifying(true);
    try {
      await verify2FA(verificationCode, twoFAData.pending_token);
      toast.success('Connexion réussie !');
      navigate(from, { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || 'Code invalide ou expiré';
      toast.error(message);
      setVerificationCode('');
    } finally {
      setIsVerifying(false);
    }
  };

  const resendCode = async () => {
    if (twoFAData?.method !== 'email') return;
    
    setIsSendingCode(true);
    try {
      await axios.post(`${API_URL}/api/auth/2fa/send-code?email=${encodeURIComponent(twoFAData.email)}`);
      toast.success('Nouveau code envoyé par email');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du code');
    } finally {
      setIsSendingCode(false);
    }
  };

  const cancel2FA = () => {
    setShow2FA(false);
    setTwoFAData(null);
    setVerificationCode('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex items-center justify-center p-4">
      {/* 2FA Verification Modal */}
      <AnimatePresence>
        {show2FA && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card w-full max-w-md rounded-3xl border border-border shadow-2xl p-8"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h2 className="font-heading text-2xl mb-2">Vérification 2FA</h2>
                <p className="text-muted-foreground text-sm">
                  {twoFAData?.method === 'email' 
                    ? 'Entrez le code reçu par email'
                    : 'Entrez le code de votre application d\'authentification'}
                </p>
              </div>

              <form onSubmit={handle2FAVerify} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="code" className="text-sm font-medium flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-muted-foreground" />
                    Code à 6 chiffres
                  </Label>
                  <Input
                    data-testid="2fa-code-input"
                    id="code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-2xl font-mono tracking-[0.5em] py-6 rounded-xl"
                    autoFocus
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={cancel2FA}
                    className="flex-1 rounded-xl py-5"
                    data-testid="2fa-cancel-btn"
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    disabled={isVerifying || verificationCode.length !== 6}
                    className="flex-1 rounded-xl py-5 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500"
                    data-testid="2fa-verify-btn"
                  >
                    {isVerifying ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Vérification...
                      </>
                    ) : (
                      'Vérifier'
                    )}
                  </Button>
                </div>

                {twoFAData?.method === 'email' && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={resendCode}
                      disabled={isSendingCode}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="2fa-resend-btn"
                    >
                      {isSendingCode ? 'Envoi en cours...' : 'Renvoyer le code'}
                    </button>
                  </div>
                )}
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Button
          data-testid="back-button"
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 rounded-full"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </Button>

        <div className="bg-card p-8 rounded-3xl border border-border shadow-xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <h1 className="font-heading text-2xl mb-2">
              {isLogin ? 'Connexion' : 'Inscription'}
            </h1>
            <p className="text-muted-foreground text-sm">
              {isLogin 
                ? 'Retrouvez vos lectures personnalisées' 
                : 'Rejoignez la communauté Lumina'}
            </p>
          </div>

          {/* Google Auth */}
          <Button
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full mb-6 py-6 rounded-xl border-2 hover:bg-secondary/30"
          >
            <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continuer avec Google
          </Button>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Ou</span>
            </div>
          </div>

          {/* Classic Form */}
          <form onSubmit={handleClassicAuth} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="email-input"
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 py-5 rounded-xl"
                  required
                />
              </div>
            </div>

            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-sm font-medium">
                    Prénom <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="firstname-input"
                      id="firstName"
                      type="text"
                      placeholder="Votre prénom"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="pl-10 py-5 rounded-xl"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Genre <span className="text-muted-foreground text-xs">(optionnel)</span>
                  </Label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      data-testid="gender-m-btn"
                      onClick={() => setFormData({ ...formData, gender: 'M' })}
                      className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition-all ${
                        formData.gender === 'M' 
                          ? 'border-blue-500 bg-blue-50 text-blue-700' 
                          : 'border-border hover:border-blue-300 hover:bg-blue-50/50'
                      }`}
                    >
                      <Users className="h-4 w-4" />
                      <span className="font-medium">Masculin</span>
                    </button>
                    <button
                      type="button"
                      data-testid="gender-f-btn"
                      onClick={() => setFormData({ ...formData, gender: 'F' })}
                      className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition-all ${
                        formData.gender === 'F' 
                          ? 'border-pink-500 bg-pink-50 text-pink-700' 
                          : 'border-border hover:border-pink-300 hover:bg-pink-50/50'
                      }`}
                    >
                      <Users className="h-4 w-4" />
                      <span className="font-medium">Féminin</span>
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="birthDate" className="text-sm font-medium">
                    Date de naissance <span className="text-muted-foreground text-xs">(optionnel - pour personnaliser ASTROZODIAC/NUMEROLOGIA)</span>
                  </Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="birthdate-input"
                      id="birthDate"
                      type="date"
                      value={formData.birthDate}
                      onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                      className="pl-10 py-5 rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium">
                    Téléphone <span className="text-muted-foreground text-xs">(optionnel - communauté WhatsApp)</span>
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="phone-input"
                      id="phone"
                      type="tel"
                      placeholder="+33 6 12 34 56 78"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="pl-10 py-5 rounded-xl"
                    />
                  </div>
                </div>

                {/* Simple Math Captcha */}
                <div className="space-y-2">
                  <Label htmlFor="captcha" className="text-sm font-medium">
                    Vérification anti-robot <span className="text-destructive">*</span>
                  </Label>
                  <div className="bg-secondary/30 p-4 rounded-xl border border-border">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Calculator className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">Résolvez ce calcul :</span>
                      </div>
                      <button
                        type="button"
                        onClick={refreshCaptcha}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        Nouveau calcul
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-card px-4 py-3 rounded-lg border border-border text-lg font-mono font-bold text-primary">
                        {captcha.num1} {captcha.operation} {captcha.num2} = ?
                      </div>
                      <Input
                        data-testid="captcha-input"
                        id="captcha"
                        type="number"
                        placeholder="?"
                        value={captchaAnswer}
                        onChange={(e) => setCaptchaAnswer(e.target.value)}
                        className="w-24 py-5 rounded-xl text-center text-lg font-bold"
                        required
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            <Button
              data-testid="submit-btn"
              type="submit"
              disabled={isLoading}
              className="w-full py-6 rounded-xl bg-primary hover:bg-primary/90 text-lg font-semibold"
            >
              {isLoading ? 'Chargement...' : (isLogin ? 'Se connecter' : "S'inscrire")}
            </Button>
          </form>

          {/* Toggle Login/Register */}
          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-btn"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {isLogin 
                ? "Pas encore de compte ? S'inscrire" 
                : 'Déjà un compte ? Se connecter'}
            </button>
          </div>
        </div>

        {/* WhatsApp Community Info */}
        {!isLogin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-6 p-4 bg-green-50 rounded-2xl border border-green-200 text-center"
          >
            <p className="text-sm text-green-800">
              <Phone className="inline h-4 w-4 mr-2" />
              Le numéro de téléphone vous permet de rejoindre notre communauté WhatsApp pour des échanges et découvertes !
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default AuthPage;
